import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import {
  LayoutDashboard, Users, FileText, CheckCircle, XCircle, Share2, 
  Calculator, LogOut, Eye, Send, Copy, ExternalLink, Search, Plus, AlertCircle, Clock, ChevronRight, Calendar
} from 'lucide-react';

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/users', label: 'Pending Users', icon: Users },
    { path: '/admin/standins', label: 'All Stand-Ins', icon: Users },
    { path: '/admin/find-match', label: 'Find Match', icon: Search },
    { path: '/admin/requests', label: 'Requests', icon: FileText },
    { path: '/admin/invoices', label: 'Invoices', icon: FileText },
    { path: '/admin/share', label: 'Share Profiles', icon: Share2 },
    { path: '/admin/settings', label: 'Settings', icon: Calculator },
    { path: '/admin/calculator', label: 'Chit Calculator', icon: Calculator },
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
    toast.success('Logged out successfully');
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#121212] border-r border-white/5 fixed h-screen overflow-y-auto">
        <div className="p-6">
          <Link to="/" className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_set-roster/artifacts/4vngv49q_2ndTeamStandinsLogoWhite.png" 
              alt="2nd Team Casting" 
              className="h-24 w-auto"
            />
          </Link>

          <div className="px-3 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs font-semibold rounded mb-6 text-center uppercase tracking-wider">
            Admin Panel
          </div>

          <nav className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive 
                      ? 'bg-[#D4AF37]/10 text-[#D4AF37]' 
                      : 'text-white/60 hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-white/5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <span className="text-black font-bold">{user?.full_name?.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.full_name}</p>
              <p className="text-[#D4AF37] text-sm">Admin</p>
            </div>
          </div>
          <Button
            variant="ghost"
            onClick={handleLogout}
            className="w-full justify-start text-white/60 hover:text-white hover:bg-white/5"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Sign Out
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64 p-8">
        <Routes>
          <Route index element={<DashboardOverview token={token} />} />
          <Route path="users" element={<PendingUsers token={token} />} />
          <Route path="standins" element={<AllStandIns token={token} />} />
          <Route path="find-match" element={<FindMatch token={token} />} />
          <Route path="requests" element={<AllRequests token={token} />} />
          <Route path="invoices" element={<InvoiceManager token={token} />} />
          <Route path="share" element={<ShareProfiles token={token} />} />
          <Route path="settings" element={<GoogleSettings token={token} />} />
          <Route path="calculator" element={<ChitCalculator token={token} />} />
        </Routes>
      </main>
    </div>
  );
};

// Dashboard Overview
const DashboardOverview = ({ token }) => {
  const [stats, setStats] = useState({
    total_standins: 0,
    pending_standins: 0,
    total_productions: 0,
    pending_requests: 0,
    active_bookings: 0
  });
  const [loading, setLoading] = useState(true);
  const [waStatus, setWaStatus] = useState(null);

  useEffect(() => {
    fetchStats();
    fetchWaTokenStatus();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API}/stats/admin`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWaTokenStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/whatsapp-token-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setWaStatus(res.data);
    } catch { /* ignore */ }
  };

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-8">Dashboard Overview</h1>

      {/* WhatsApp Token Alert */}
      {waStatus && waStatus.status !== 'active' && (
        <div className={`mb-6 p-4 rounded-lg flex items-start gap-3 ${
          waStatus.status === 'expired' ? 'bg-red-500/10 border border-red-500/30' : 'bg-[#F59E0B]/10 border border-[#F59E0B]/30'
        }`} data-testid="wa-token-alert">
          <AlertCircle className={`w-5 h-5 flex-shrink-0 mt-0.5 ${waStatus.status === 'expired' ? 'text-red-400' : 'text-[#F59E0B]'}`} />
          <div>
            <p className={`font-medium ${waStatus.status === 'expired' ? 'text-red-400' : 'text-[#F59E0B]'}`}>
              {waStatus.status === 'expired' ? 'WhatsApp Token Expired' : 'WhatsApp Issue'}
            </p>
            <p className={`text-sm mt-1 ${waStatus.status === 'expired' ? 'text-red-400/70' : 'text-[#F59E0B]/70'}`}>
              {waStatus.message}
            </p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <p className="text-[#D4AF37] font-['Playfair_Display'] text-4xl font-bold">{stats.total_standins}</p>
          <p className="text-white/60 text-sm mt-2">Total Stand-Ins</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <p className="text-[#F59E0B] font-['Playfair_Display'] text-4xl font-bold">{stats.pending_standins}</p>
          <p className="text-white/60 text-sm mt-2">Pending Approval</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <p className="text-[#D4AF37] font-['Playfair_Display'] text-4xl font-bold">{stats.total_productions}</p>
          <p className="text-white/60 text-sm mt-2">Productions</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <p className="text-[#F59E0B] font-['Playfair_Display'] text-4xl font-bold">{stats.pending_requests}</p>
          <p className="text-white/60 text-sm mt-2">Pending Requests</p>
        </div>
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <p className="text-[#10B981] font-['Playfair_Display'] text-4xl font-bold">{stats.active_bookings}</p>
          <p className="text-white/60 text-sm mt-2">Active Bookings</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <Link to="/admin/users" className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-colors">
          <h3 className="text-white font-semibold mb-2">Review Pending Users</h3>
          <p className="text-white/60 text-sm">Approve or reject new registrations</p>
        </Link>
        <Link to="/admin/requests" className="bg-[#121212] border border-white/5 rounded-xl p-6 hover:border-[#D4AF37]/30 transition-colors">
          <h3 className="text-white font-semibold mb-2">Match Stand-Ins</h3>
          <p className="text-white/60 text-sm">Match stand-ins to production requests</p>
        </Link>
      </div>
    </div>
  );
};

// Pending Users
const PendingUsers = ({ token }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchPendingUsers();
  }, []);

  const fetchPendingUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/pending-users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching pending users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (userId) => {
    try {
      await axios.put(`${API}/admin/approve-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User approved');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to approve user');
    }
  };

  const handleReject = async (userId) => {
    try {
      await axios.put(`${API}/admin/reject-user/${userId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User rejected');
      fetchPendingUsers();
    } catch (error) {
      toast.error('Failed to reject user');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-8">Pending Users</h1>

      {users.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <CheckCircle className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
          <p className="text-white">All caught up! No pending users.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {users.map((user) => (
            <div key={user.id} className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-white font-semibold">{user.full_name}</h3>
                  <p className="text-white/60 text-sm">{user.email}</p>
                  <span className={`mt-2 inline-block px-2 py-1 text-xs rounded capitalize ${
                    user.role === 'standin' ? 'bg-[#D4AF37]/20 text-[#D4AF37]' : 'bg-blue-500/20 text-blue-400'
                  }`}>
                    {user.role}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => handleApprove(user.id)}
                    className="bg-[#10B981] hover:bg-[#10B981]/80 text-white"
                    data-testid={`approve-${user.id}`}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Approve
                  </Button>
                  <Button
                    onClick={() => handleReject(user.id)}
                    variant="outline"
                    className="border-red-500 text-red-500 hover:bg-red-500/10"
                    data-testid={`reject-${user.id}`}
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// All Stand-Ins
const AllStandIns = ({ token }) => {
  const [standins, setStandins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStandin, setSelectedStandin] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [flagging, setFlagging] = useState(false);

  useEffect(() => {
    fetchStandins();
  }, []);

  const fetchStandins = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-standins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStandins(response.data);
    } catch (error) {
      console.error('Error fetching stand-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewDetail = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/standin-detail/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDetailData(res.data);
      setSelectedStandin(userId);
    } catch (error) {
      toast.error('Failed to load detail');
    }
  };

  const flagPhrases = async (needsTraining, notes) => {
    setFlagging(true);
    try {
      await axios.put(`${API}/admin/flag-radio-phrases/${selectedStandin}`, {
        needs_training: needsTraining,
        admin_notes: notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(needsTraining ? 'Marked as needs training' : 'Marked as experienced');
      setDetailData(prev => ({
        ...prev,
        profile: {
          ...prev.profile,
          radio_phrases_review: { needs_training: needsTraining, admin_notes: notes }
        }
      }));
    } catch (error) {
      toast.error('Failed to flag');
    } finally {
      setFlagging(false);
    }
  };

  const proxyImg = (url) => {
    if (!url) return '';
    if (url.startsWith('/') || url.includes(window.location.host)) return url;
    return `${API}/image-proxy?url=${encodeURIComponent(url)}`;
  };

  const fmtHeight = (m) => {
    if (!m) return '';
    if (m.height_ft) return `${m.height_ft}'${Math.round(m.height_in || 0)}"`;
    if (m.height_cm) return `${Math.round(m.height_cm)}cm`;
    return '';
  };

  const filteredStandins = standins.filter(s => 
    s.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.hair_color?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    s.gender?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">All Stand-Ins</h1>
          <p className="text-white/40 text-sm mt-1">{filteredStandins.length} of {standins.length} profiles</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, hair, gender..."
            className="pl-10 bg-[#0A0A0A] border-white/10 text-white w-72"
            data-testid="search-standins"
          />
        </div>
      </div>

      {/* CV Detail Modal */}
      {detailData && (
        <div className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4" onClick={() => { setDetailData(null); setSelectedStandin(null); }}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="standin-detail-modal">
            
            {/* Header Bar */}
            <div className="bg-[#1a1a1a] px-6 py-4 rounded-t-xl flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="font-['Playfair_Display'] text-[#D4AF37] text-xl font-bold">2nd Team</span>
                <span className="text-white/40 text-sm">Stand-In Profile</span>
              </div>
              <button onClick={() => { setDetailData(null); setSelectedStandin(null); }} className="text-white/40 hover:text-white">
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6">
              {/* Top Section: Name + Photos side by side */}
              <div className="flex gap-8 mb-8">
                {/* Left: Info */}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-1">{detailData.profile?.full_name}</h1>
                  <p className="text-[#D4AF37] text-sm font-medium capitalize mb-6">{detailData.profile?.experience_level?.replace(/_/g, ' ') || 'Stand-In'}</p>
                  
                  {/* Key Details Table */}
                  <table className="w-full text-sm mb-4">
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700 w-40">Gender</td>
                        <td className="py-2 text-gray-900 capitalize">{detailData.profile?.gender || '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700">Height</td>
                        <td className="py-2 text-gray-900">{detailData.profile?.measurements?.height_ft ? `${detailData.profile.measurements.height_ft}'${Math.round(detailData.profile.measurements.height_in || 0)}"` : '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700">Hair Colour</td>
                        <td className="py-2 text-gray-900 capitalize">{detailData.profile?.hair_color || '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700">Skin Tone / Ethnicity</td>
                        <td className="py-2 text-gray-900 capitalize">{detailData.profile?.skin_tone?.replace('_', ' ') || '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700">Email</td>
                        <td className="py-2 text-gray-900">{detailData.profile?.contact_email || detailData.profile?.email || '-'}</td>
                      </tr>
                      <tr className="border-b border-gray-100">
                        <td className="py-2 font-semibold text-gray-700">Phone</td>
                        <td className="py-2 text-gray-900">{detailData.profile?.phone_number || detailData.profile?.phone || '-'}</td>
                      </tr>
                      <tr>
                        <td className="py-2 font-semibold text-gray-700">Address</td>
                        <td className="py-2 text-gray-900">{detailData.profile?.address || '-'}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                
                {/* Right: Headshot */}
                <div className="w-52 flex-shrink-0">
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Headshot</p>
                  {(() => {
                    const imageFiles = detailData.files?.filter(f => f.file_type === 'image') || [];
                    const headshot = imageFiles.length > 0 
                      ? `${API}/files/${imageFiles[0].storage_path}?auth=${token}`
                      : detailData.profile?.headshot_url;
                    return headshot ? (
                      <img src={proxyImg(headshot)} alt="Headshot" className="w-full aspect-[3/4] object-cover rounded-lg shadow-md" />
                    ) : (
                      <div className="w-full aspect-[3/4] rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-5xl font-bold">
                        {detailData.profile?.full_name?.[0] || '?'}
                      </div>
                    );
                  })()}
                </div>
              </div>

              {/* Full Body Shot */}
              <div className="mb-8">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Full Body Shot</p>
                <div className="flex gap-4">
                  {(() => {
                    const imageFiles = detailData.files?.filter(f => f.file_type === 'image') || [];
                    const fullBody = imageFiles.length > 1
                      ? `${API}/files/${imageFiles[1].storage_path}?auth=${token}`
                      : detailData.profile?.full_body_url;
                    const extras = imageFiles.slice(2).map(f => `${API}/files/${f.storage_path}?auth=${token}`);
                    return (
                      <>
                        {fullBody ? (
                          <img src={proxyImg(fullBody)} alt="Full body" className="h-64 object-cover rounded-lg shadow-md" />
                        ) : (
                          <div className="h-64 w-44 rounded-lg bg-gray-100 flex items-center justify-center text-gray-300 text-sm">No photo</div>
                        )}
                        {extras.map((url, i) => (
                          <img key={i} src={url} alt={`Photo ${i+3}`} className="h-64 object-cover rounded-lg shadow-md" />
                        ))}
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Divider */}
              <div className="border-t-2 border-gray-900 mb-6" />

              {/* Measurements */}
              <div className="mb-8">
                <h2 className="text-lg font-bold text-gray-900 mb-4">Measurements</h2>
                <div className="grid grid-cols-2 gap-x-12 gap-y-1">
                  {[
                    { label: 'Height (feet.inches)', value: detailData.profile?.measurements?.height_ft ? `${detailData.profile.measurements.height_ft}.${Math.round(detailData.profile.measurements.height_in || 0)}` : null },
                    { label: 'Height (cm)', value: detailData.profile?.measurements?.height_cm ? `${Math.round(detailData.profile.measurements.height_cm)}` : null },
                    { label: 'Chest/Bust', value: detailData.profile?.measurements?.chest_bust },
                    { label: 'Waist', value: detailData.profile?.measurements?.waist },
                    { label: 'Hips', value: detailData.profile?.measurements?.hips },
                    { label: 'Neck', value: detailData.profile?.measurements?.neck },
                    { label: 'Inside Leg', value: detailData.profile?.measurements?.inside_leg },
                    { label: 'Shoe Size', value: detailData.profile?.measurements?.shoe_size },
                  ].map((m, i) => (
                    <div key={i} className="flex justify-between py-1.5 border-b border-gray-100 text-sm">
                      <span className="font-semibold text-gray-700">{m.label}</span>
                      <span className="text-gray-900">{m.value || '-'}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Previous Productions */}
              {detailData.profile?.experiences?.length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-bold text-gray-900 mb-4">Previous Productions</h2>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b-2 border-gray-900">
                        <th className="text-left py-2 font-semibold text-gray-700">Production</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Actor</th>
                        <th className="text-left py-2 font-semibold text-gray-700">Crowd 2nd / 1st AD</th>
                        <th className="text-right py-2 font-semibold text-gray-700">Days</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detailData.profile.experiences.map((exp, i) => (
                        <tr key={i} className="border-b border-gray-100">
                          <td className="py-2 text-gray-900 font-medium">{exp.production_name || exp.production || (typeof exp === 'string' ? exp : '-')}</td>
                          <td className="py-2 text-gray-600">{exp.actor_name || '-'}</td>
                          <td className="py-2 text-gray-600">{exp.crowd_2nd || exp.first_ad || '-'}</td>
                          <td className="py-2 text-gray-600 text-right">{exp.days_worked || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Documents */}
              {detailData.files?.filter(f => f.file_type === 'document').length > 0 && (
                <div className="mb-8">
                  <h2 className="text-lg font-bold text-gray-900 mb-3">Documents</h2>
                  <div className="space-y-2">
                    {detailData.files.filter(f => f.file_type === 'document').map(f => (
                      <div key={f.id} className="flex items-center gap-3 bg-gray-50 rounded-lg p-3">
                        <FileText className="w-4 h-4 text-gray-400" />
                        <span className="text-gray-700 text-sm">{f.original_filename}</span>
                        <span className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-500 capitalize">{f.doc_type?.replace('_', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Radio Phrases */}
              <div className="mb-4">
                <h2 className="text-lg font-bold text-gray-900 mb-3">Radio Phrases</h2>
                {detailData.profile?.radio_phrases ? (
                  <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                    {[1, 2, 3, 4].map(n => (
                      <div key={n} className="flex gap-2 text-sm">
                        <span className="text-[#D4AF37] font-mono font-bold w-5">{n}.</span>
                        <span className="text-gray-700">{detailData.profile.radio_phrases[`phrase_${n}`] || '(empty)'}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-400 text-sm italic">Not submitted yet</p>
                )}
                {detailData.profile?.radio_phrases_review && (
                  <div className={`mt-2 px-3 py-2 rounded text-sm ${
                    detailData.profile.radio_phrases_review.needs_training 
                      ? 'bg-amber-50 text-amber-700 border border-amber-200' 
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                  }`}>
                    {detailData.profile.radio_phrases_review.needs_training ? 'Needs Training' : 'Experienced'}
                  </div>
                )}
                <div className="flex gap-2 mt-3">
                  <Button onClick={() => flagPhrases(false, '')} disabled={flagging} size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs">Experienced</Button>
                  <Button onClick={() => flagPhrases(true, '')} disabled={flagging} size="sm" className="bg-amber-500 hover:bg-amber-600 text-white text-xs">Needs Training</Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stand-in Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredStandins.map((standin) => (
          <div
            key={standin.id}
            className="bg-[#121212] border border-white/5 rounded-xl overflow-hidden hover:border-[#D4AF37]/30 transition-all cursor-pointer group"
            onClick={() => viewDetail(standin.user_id)}
            data-testid={`standin-card-${standin.id}`}
          >
            {/* Photo */}
            <div className="aspect-[4/3] bg-[#0A0A0A] overflow-hidden">
              {standin.headshot_url ? (
                <img src={proxyImg(standin.headshot_url)} alt={standin.full_name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#D4AF37]/30 text-6xl font-bold">
                  {standin.full_name?.[0] || '?'}
                </div>
              )}
            </div>
            {/* Info */}
            <div className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <h3 className="text-white font-semibold">{standin.full_name}</h3>
                  <p className="text-white/40 text-xs capitalize">{standin.experience_level?.replace(/_/g, ' ') || 'Stand-In'}</p>
                </div>
                <span className={`px-2 py-0.5 text-xs rounded capitalize ${
                  standin.status === 'approved' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#F59E0B]/20 text-[#F59E0B]'
                }`}>{standin.status}</span>
              </div>
              {/* Key stats */}
              <div className="grid grid-cols-2 gap-2 text-xs mt-3">
                <div className="flex justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                  <span className="text-white/40">Gender</span>
                  <span className="text-white capitalize">{standin.gender || '-'}</span>
                </div>
                <div className="flex justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                  <span className="text-white/40">Height</span>
                  <span className="text-white">{fmtHeight(standin.measurements) || '-'}</span>
                </div>
                <div className="flex justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                  <span className="text-white/40">Hair</span>
                  <span className="text-white capitalize">{standin.hair_color || '-'}</span>
                </div>
                <div className="flex justify-between bg-[#0A0A0A] rounded px-2 py-1.5">
                  <span className="text-white/40">Skin</span>
                  <span className="text-white capitalize">{standin.skin_tone?.replace('_', ' ') || '-'}</span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// All Requests
const AllRequests = ({ token }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matchingId, setMatchingId] = useState(null);
  const [matchResults, setMatchResults] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(response.data);
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoading(false);
    }
  };

  const findMatches = async (requestId) => {
    setMatchingId(requestId);
    try {
      const response = await axios.post(`${API}/admin/find-matches/${requestId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMatchResults({ requestId, ...response.data });
    } catch (error) {
      toast.error('Failed to find matches');
    } finally {
      setMatchingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-8">Production Requests</h1>

      {matchResults && (
        <div className="bg-[#121212] border border-[#D4AF37]/30 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-white font-semibold text-lg">
              Match Results ({matchResults.total_matches} found)
            </h2>
            <Button variant="ghost" onClick={() => setMatchResults(null)} className="text-white/60 hover:text-white" data-testid="close-match-results">
              <XCircle className="w-5 h-5" />
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
            {matchResults.matches?.slice(0, 15).map((match) => (
              <div key={match.standin_id} className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
                <div className="flex items-center gap-3 mb-3">
                  {match.headshot_url ? (
                    <img src={match.headshot_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                      <span className="text-[#D4AF37] font-bold">{match.first_name?.charAt(0)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium truncate">{match.first_name}</p>
                    <p className="text-white/40 text-xs capitalize">{match.gender} | {match.hair_color}</p>
                  </div>
                  <div className={`px-2 py-1 rounded text-xs font-bold ${
                    match.match_score >= 80 ? 'bg-[#10B981]/20 text-[#10B981]' :
                    match.match_score >= 50 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                    'bg-white/10 text-white/60'
                  }`}>
                    {match.match_score}%
                  </div>
                </div>
                {match.measurements && (
                  <p className="text-white/40 text-xs">
                    {match.measurements.height_ft && `${match.measurements.height_ft}'${match.measurements.height_in || 0}"`}
                    {match.measurements.chest_bust && ` | Chest: ${match.measurements.chest_bust}"`}
                  </p>
                )}
                {match.experiences?.length > 0 && (
                  <p className="text-white/30 text-xs mt-1 truncate">{match.experiences[0]?.production_name}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {requests.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No requests yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map((request) => (
            <div key={request.id} className="bg-[#121212] border border-white/5 rounded-xl p-6">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="text-white font-semibold text-lg">{request.production_name}</h3>
                  {request.actor_name && (
                    <p className="text-[#D4AF37] text-sm">Looking for: {request.actor_name} {request.performer_type || 'stand-in'}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-3">
                    {request.performer_type && <span className="px-2 py-1 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded capitalize">{request.performer_type.replace('_', ' ')}</span>}
                    {request.gender && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.gender}</span>}
                    {request.hair_color && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.hair_color}</span>}
                    {request.skin_tone && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded capitalize">{request.skin_tone?.replace('_', ' ')}</span>}
                    {request.cast_age && <span className="px-2 py-1 bg-white/5 text-white/60 text-xs rounded">Age: {request.cast_age}</span>}
                  </div>
                  <p className="text-white/60 text-sm mt-2">
                    {request.start_date && request.end_date ? `${request.start_date} to ${request.end_date}` : `${request.dates_needed?.length || 0} day(s)`} | {request.location} | £{request.rate_offered}/day
                  </p>
                </div>
                <div className="flex gap-2">
                  <Button 
                    onClick={() => findMatches(request.id)}
                    disabled={matchingId === request.id}
                    className="gold-gradient text-black font-semibold" 
                    data-testid={`match-${request.id}`}
                  >
                    <Users className="w-4 h-4 mr-2" />
                    {matchingId === request.id ? 'Matching...' : 'Find Matches'}
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Find Match - Quick Search
const FindMatch = ({ token }) => {
  const [criteria, setCriteria] = useState({
    gender: '',
    height_min_cm: '',
    height_max_cm: '',
    hair_color: '',
    skin_tone: ''
  });
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleSearch = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/quick-match`, {
        gender: criteria.gender || null,
        height_min_cm: criteria.height_min_cm ? parseFloat(criteria.height_min_cm) : null,
        height_max_cm: criteria.height_max_cm ? parseFloat(criteria.height_max_cm) : null,
        hair_color: criteria.hair_color || null,
        skin_tone: criteria.skin_tone || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(response.data);
    } catch (error) {
      toast.error('Search failed');
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setCriteria({ gender: '', height_min_cm: '', height_max_cm: '', hair_color: '', skin_tone: '' });
    setResults(null);
  };

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Find a Match</h1>
      <p className="text-white/60 mb-8">Search stand-ins by characteristics</p>

      {/* Search Filters */}
      <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div>
            <Label className="text-white/80 mb-2 block text-sm">Gender</Label>
            <Select value={criteria.gender} onValueChange={(v) => setCriteria({ ...criteria, gender: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="match-gender-select">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/80 mb-2 block text-sm">Hair Colour</Label>
            <Select value={criteria.hair_color} onValueChange={(v) => setCriteria({ ...criteria, hair_color: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="match-hair-select">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="black">Black</SelectItem>
                <SelectItem value="brown">Brown</SelectItem>
                <SelectItem value="blonde">Blonde</SelectItem>
                <SelectItem value="red">Red</SelectItem>
                <SelectItem value="grey">Grey</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/80 mb-2 block text-sm">Skin Tone</Label>
            <Select value={criteria.skin_tone} onValueChange={(v) => setCriteria({ ...criteria, skin_tone: v })}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white" data-testid="match-skin-select">
                <SelectValue placeholder="Any" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="very_fair">Very Fair</SelectItem>
                <SelectItem value="fair">Fair</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="olive">Olive</SelectItem>
                <SelectItem value="tan">Tan</SelectItem>
                <SelectItem value="dark">Dark</SelectItem>
                <SelectItem value="very_dark">Very Dark</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-white/80 mb-2 block text-sm">Height Min (cm)</Label>
            <Input
              type="number"
              value={criteria.height_min_cm}
              onChange={(e) => setCriteria({ ...criteria, height_min_cm: e.target.value })}
              placeholder="160"
              className="bg-[#0A0A0A] border-white/10 text-white"
              data-testid="match-height-min"
            />
          </div>
          <div>
            <Label className="text-white/80 mb-2 block text-sm">Height Max (cm)</Label>
            <Input
              type="number"
              value={criteria.height_max_cm}
              onChange={(e) => setCriteria({ ...criteria, height_max_cm: e.target.value })}
              placeholder="190"
              className="bg-[#0A0A0A] border-white/10 text-white"
              data-testid="match-height-max"
            />
          </div>
        </div>
        <div className="flex gap-3 mt-4">
          <Button onClick={handleSearch} disabled={loading} className="gold-gradient text-black font-semibold" data-testid="search-match-btn">
            <Search className="w-4 h-4 mr-2" />
            {loading ? 'Searching...' : 'Search'}
          </Button>
          <Button onClick={clearFilters} variant="outline" className="border-white/20 text-white/60 hover:text-white" data-testid="clear-filters-btn">
            Clear
          </Button>
        </div>
      </div>

      {/* Results */}
      {results && (
        <div>
          <p className="text-white/60 mb-4">{results.total_matches} stand-in(s) found</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {results.matches?.map((match) => (
              <div key={match.standin_id} className="bg-[#121212] border border-white/5 rounded-xl p-5 hover:border-[#D4AF37]/30 transition-colors" data-testid={`match-card-${match.standin_id}`}>
                <div className="flex items-start gap-4">
                  {match.headshot_url ? (
                    <img src={match.headshot_url} alt="" className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-16 h-16 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
                      <span className="text-[#D4AF37] font-bold text-xl">{match.first_name?.charAt(0)}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-white font-semibold">{match.first_name}</p>
                      <span className={`px-2 py-1 rounded text-xs font-bold ${
                        match.match_score >= 80 ? 'bg-[#10B981]/20 text-[#10B981]' :
                        match.match_score >= 50 ? 'bg-[#F59E0B]/20 text-[#F59E0B]' :
                        'bg-white/10 text-white/60'
                      }`}>
                        {match.match_score}%
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {match.gender && <span className="px-1.5 py-0.5 bg-white/5 text-white/50 text-xs rounded capitalize">{match.gender}</span>}
                      {match.hair_color && <span className="px-1.5 py-0.5 bg-white/5 text-white/50 text-xs rounded capitalize">{match.hair_color}</span>}
                      {match.skin_tone && <span className="px-1.5 py-0.5 bg-white/5 text-white/50 text-xs rounded capitalize">{match.skin_tone?.replace('_', ' ')}</span>}
                    </div>
                    {match.measurements && (
                      <p className="text-white/40 text-xs mt-2">
                        {match.measurements.height_ft != null && `${match.measurements.height_ft}'${match.measurements.height_in || 0}"`}
                        {match.measurements.chest_bust && ` | Chest: ${match.measurements.chest_bust}"`}
                        {match.measurements.waist && ` | Waist: ${match.measurements.waist}"`}
                      </p>
                    )}
                    {match.experience_level && (
                      <p className="text-[#D4AF37]/70 text-xs mt-1 capitalize">{match.experience_level.replace(/_/g, ' ')}</p>
                    )}
                    {match.experiences?.length > 0 && (
                      <p className="text-white/30 text-xs mt-1 truncate">{match.experiences[0]?.production_name}</p>
                    )}
                    {match.city && <p className="text-white/30 text-xs mt-1">{match.city}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};


// Invoice Manager
const InvoiceManager = ({ token }) => {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showWeekly, setShowWeekly] = useState(false);
  const [weeklyForm, setWeeklyForm] = useState({ client_name: '', project_name: '', start_date: '', end_date: '' });
  const [generatingWeekly, setGeneratingWeekly] = useState(false);
  const [standins, setStandins] = useState([]);
  const [creating, setCreating] = useState(false);
  const [previewInvoice, setPreviewInvoice] = useState(null);

  // Format date to UK format DD.MM.YYYY
  const formatDateUK = (dateStr) => {
    if (!dateStr) return '';
    try {
      // Handle DD/MM/YYYY format
      if (dateStr.includes('/')) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const yr = parts[2].length === 4 ? parts[2] : `20${parts[2]}`;
          return `${parts[0]}.${parts[1]}.${yr}`;
        }
      }
      // Handle YYYY-MM-DD or ISO format
      const d = new Date(dateStr + (dateStr.length === 10 && !dateStr.includes('T') ? 'T00:00:00' : ''));
      if (isNaN(d.getTime())) return dateStr;
      const day = String(d.getDate()).padStart(2, '0');
      const month = String(d.getMonth() + 1).padStart(2, '0');
      const year = d.getFullYear();
      return `${day}.${month}.${year}`;
    } catch { return dateStr; }
  };

  // New invoice form state
  const [form, setForm] = useState({
    client_name: '',
    client_address: '',
    project_name: '',
    due_date: '',
    notes: '',
  });
  const emptyItem = { standin_user_id: '', role: 'Stand-in', date_worked: '', day_rate: 250, quantity: 1 };
  const [lineItems, setLineItems] = useState([{ ...emptyItem }]);

  useEffect(() => {
    fetchInvoices();
    fetchStandins();
  }, []);

  const fetchInvoices = async () => {
    try {
      const res = await axios.get(`${API}/admin/invoices`, { headers: { Authorization: `Bearer ${token}` } });
      setInvoices(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchStandins = async () => {
    try {
      const res = await axios.get(`${API}/admin/all-standins`, { headers: { Authorization: `Bearer ${token}` } });
      setStandins(res.data);
    } catch (e) { console.error(e); }
  };

  const addLineItem = () => setLineItems([...lineItems, { ...emptyItem }]);

  const removeLineItem = (idx) => {
    if (lineItems.length <= 1) return;
    setLineItems(lineItems.filter((_, i) => i !== idx));
  };

  const updateLineItem = (idx, field, value) => {
    const updated = [...lineItems];
    updated[idx] = { ...updated[idx], [field]: value };
    // Auto-fill rate when standin selected
    if (field === 'standin_user_id') {
      const s = standins.find(st => st.user_id === value);
      if (s?.preferred_rate) updated[idx].day_rate = s.preferred_rate;
    }
    setLineItems(updated);
  };

  const calcSubtotal = () => lineItems.reduce((sum, item) => sum + (parseFloat(item.day_rate || 0) * parseFloat(item.quantity || 0)), 0);
  const calcHoliday = () => 0;
  const calcTotal = () => calcSubtotal();

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.project_name) { toast.error('Client name and project name are required'); return; }
    if (lineItems.some(i => !i.standin_user_id || !i.date_worked)) { toast.error('Each line item needs a stand-in and date'); return; }
    setCreating(true);
    try {
      await axios.post(`${API}/admin/invoices`, {
        client_name: form.client_name,
        client_address: form.client_address,
        project_name: form.project_name,
        due_date: form.due_date,
        notes: form.notes,
        line_items: lineItems.map(i => ({
          standin_user_id: i.standin_user_id,
          role: i.role,
          date_worked: i.date_worked,
          day_rate: parseFloat(i.day_rate),
          quantity: parseFloat(i.quantity),
        })),
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Invoice created');
      setShowCreate(false);
      setForm({ client_name: '', client_address: '', project_name: '', due_date: '', notes: '' });
      setLineItems([{ ...emptyItem }]);
      fetchInvoices();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create invoice'); }
    finally { setCreating(false); }
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.put(`${API}/admin/invoices/${id}/status?status=${status}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Invoice marked as ${status}`);
      fetchInvoices();
    } catch (e) { toast.error('Failed'); }
  };

  const updateLineItemPayment = async (invoiceId, itemIndex, paymentStatus) => {
    try {
      const dateStr = new Date().toISOString().split('T')[0];
      await axios.put(
        `${API}/admin/invoices/${invoiceId}/line-item/${itemIndex}/payment?payment_status=${paymentStatus}&payment_date=${dateStr}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Payment updated');
      fetchInvoices();
      // Refresh preview if open
      if (previewInvoice?.id === invoiceId) {
        const res = await axios.get(`${API}/admin/invoices/${invoiceId}`, { headers: { Authorization: `Bearer ${token}` } });
        setPreviewInvoice(res.data);
      }
    } catch (e) { toast.error('Failed to update payment'); }
  };

  const getStandinName = (userId) => standins.find(s => s.user_id === userId)?.full_name || userId;

  const duplicateInvoice = async (invoiceId) => {
    try {
      const res = await axios.post(`${API}/admin/invoices/${invoiceId}/duplicate`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Duplicated as ${res.data.invoice_number}`);
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to duplicate'); }
  };

  const generateWeeklyInvoice = async () => {
    if (!weeklyForm.client_name || !weeklyForm.project_name || !weeklyForm.start_date || !weeklyForm.end_date) {
      toast.error('All fields are required');
      return;
    }
    setGeneratingWeekly(true);
    try {
      const params = new URLSearchParams(weeklyForm);
      const res = await axios.post(`${API}/admin/invoices/generate-weekly?${params}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Weekly invoice ${res.data.invoice_number} created — ${res.data.line_items?.length} stand-ins`);
      setShowWeekly(false);
      setWeeklyForm({ client_name: '', project_name: '', start_date: '', end_date: '' });
      fetchInvoices();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to generate weekly invoice'); }
    finally { setGeneratingWeekly(false); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white">Invoices</h1>
        <div className="flex gap-2">
          <Button onClick={() => { setShowWeekly(!showWeekly); setShowCreate(false); }} variant="outline" className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10" data-testid="weekly-invoice-btn">
            <Calendar className="w-4 h-4 mr-2" /> Weekly Invoice
          </Button>
          <Button onClick={() => { setShowCreate(!showCreate); setShowWeekly(false); }} className="gold-gradient text-black font-semibold" data-testid="create-invoice-btn">
            <Plus className="w-4 h-4 mr-2" /> New Invoice
          </Button>
        </div>
      </div>

      {/* Weekly Invoice Generator */}
      {showWeekly && (
        <div className="bg-[#121212] border border-[#D4AF37]/20 rounded-xl p-6 mb-6">
          <h2 className="text-white font-semibold mb-4">Generate Weekly Invoice</h2>
          <p className="text-white/40 text-sm mb-4">Aggregates all stand-ins by name, days worked, and total earned for the week.</p>
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/80 mb-2 block">Client Name *</Label>
              <Input value={weeklyForm.client_name} onChange={e => setWeeklyForm({ ...weeklyForm, client_name: e.target.value })} placeholder="e.g., The Fixer CMG Limited" className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="weekly-client" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Project Name *</Label>
              <Input value={weeklyForm.project_name} onChange={e => setWeeklyForm({ ...weeklyForm, project_name: e.target.value })} placeholder="e.g., Fixer" className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="weekly-project" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Week Start *</Label>
              <Input type="date" value={weeklyForm.start_date} onChange={e => setWeeklyForm({ ...weeklyForm, start_date: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="weekly-start" />
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Week End *</Label>
              <Input type="date" value={weeklyForm.end_date} onChange={e => setWeeklyForm({ ...weeklyForm, end_date: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white h-12" data-testid="weekly-end" />
            </div>
          </div>
          <Button onClick={generateWeeklyInvoice} disabled={generatingWeekly} className="gold-gradient text-black font-semibold" data-testid="generate-weekly-btn">
            {generatingWeekly ? 'Generating...' : 'Generate Weekly Invoice'}
          </Button>
        </div>
      )}

      {/* Create Invoice Form */}
      {showCreate && (
        <form onSubmit={handleCreate} className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="text-white font-semibold mb-4">Create Invoice</h2>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Client / Production Co. *</Label>
              <Input value={form.client_name} onChange={e => setForm({ ...form, client_name: e.target.value })} required placeholder="e.g. Netflix Productions Ltd" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="invoice-client-name" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Project Name *</Label>
              <Input value={form.project_name} onChange={e => setForm({ ...form, project_name: e.target.value })} required placeholder="e.g. The Crown S7" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="invoice-project-name" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Client Address</Label>
              <Input value={form.client_address} onChange={e => setForm({ ...form, client_address: e.target.value })} placeholder="Billing address" className="bg-[#0A0A0A] border-white/10 text-white" data-testid="invoice-client-address" />
            </div>
            <div>
              <Label className="text-white/80 mb-1 block text-sm">Due Date</Label>
              <Input type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white" data-testid="invoice-due-date" />
            </div>
          </div>

          {/* Line Items */}
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-white/80 text-sm font-semibold">Line Items</Label>
              <Button type="button" onClick={addLineItem} variant="ghost" size="sm" className="text-[#D4AF37] hover:text-[#D4AF37]/80 text-xs" data-testid="add-line-item-btn">
                <Plus className="w-3 h-3 mr-1" /> Add Line
              </Button>
            </div>

            <div className="space-y-3">
              {lineItems.map((item, idx) => (
                <div key={idx} className="bg-[#0A0A0A] border border-white/5 rounded-lg p-3" data-testid={`line-item-${idx}`}>
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-3">
                      <Label className="text-white/50 text-xs block mb-1">Stand-in</Label>
                      <Select value={item.standin_user_id} onValueChange={v => updateLineItem(idx, 'standin_user_id', v)}>
                        <SelectTrigger className="bg-[#121212] border-white/10 text-white text-xs h-9" data-testid={`line-item-standin-${idx}`}>
                          <SelectValue placeholder="Select..." />
                        </SelectTrigger>
                        <SelectContent>
                          {standins.map(s => (
                            <SelectItem key={s.user_id} value={s.user_id}>
                              {s.full_name} (£{s.preferred_rate || 250})
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/50 text-xs block mb-1">Role</Label>
                      <Input value={item.role} onChange={e => updateLineItem(idx, 'role', e.target.value)} className="bg-[#121212] border-white/10 text-white text-xs h-9" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/50 text-xs block mb-1">Date Worked</Label>
                      <Input type="date" value={item.date_worked} onChange={e => updateLineItem(idx, 'date_worked', e.target.value)} className="bg-[#121212] border-white/10 text-white text-xs h-9" data-testid={`line-item-date-${idx}`} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-white/50 text-xs block mb-1">Day Rate (£)</Label>
                      <Input type="number" value={item.day_rate} onChange={e => updateLineItem(idx, 'day_rate', e.target.value)} className="bg-[#121212] border-white/10 text-white text-xs h-9" data-testid={`line-item-rate-${idx}`} />
                    </div>
                    <div className="col-span-1">
                      <Label className="text-white/50 text-xs block mb-1">Qty</Label>
                      <Input type="number" step="0.5" value={item.quantity} onChange={e => updateLineItem(idx, 'quantity', e.target.value)} className="bg-[#121212] border-white/10 text-white text-xs h-9" />
                    </div>
                    <div className="col-span-1 text-right">
                      <Label className="text-white/50 text-xs block mb-1">Amount</Label>
                      <p className="text-white font-medium text-sm h-9 flex items-center justify-end">£{(parseFloat(item.day_rate || 0) * parseFloat(item.quantity || 0)).toFixed(2)}</p>
                    </div>
                    <div className="col-span-1 flex items-end justify-center">
                      {lineItems.length > 1 && (
                        <button type="button" onClick={() => removeLineItem(idx)} className="text-red-400/60 hover:text-red-400 h-9 flex items-center" data-testid={`remove-line-item-${idx}`}>
                          <XCircle className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Notes */}
          <div className="mb-4">
            <Label className="text-white/80 mb-1 block text-sm">Notes</Label>
            <Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="bg-[#0A0A0A] border-white/10 text-white resize-none" rows={2} placeholder="Payment terms, special instructions..." />
          </div>

          {/* Live Preview */}
          {lineItems.some(i => i.standin_user_id && i.date_worked) && (
            <div className="p-4 bg-[#0A0A0A] border border-white/5 rounded-lg mb-4">
              <p className="text-white/40 text-xs uppercase tracking-wider mb-2">Preview</p>
              <div className="space-y-1 text-sm">
                {lineItems.filter(i => i.standin_user_id).map((item, idx) => (
                  <div key={idx} className="flex justify-between">
                    <span className="text-white/60">{getStandinName(item.standin_user_id)} — {formatDateUK(item.date_worked)} ({item.role})</span>
                    <span className="text-white">£{(parseFloat(item.day_rate || 0) * parseFloat(item.quantity || 0)).toFixed(2)}</span>
                  </div>
                ))}
                <div className="flex justify-between border-t border-white/5 pt-1 mt-1">
                  <span className="text-white/60">Subtotal</span>
                  <span className="text-white">£{calcSubtotal().toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-base">
                  <span className="text-[#D4AF37]">Total</span>
                  <span className="text-[#D4AF37]">£{calcTotal().toFixed(2)}</span>
                </div>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button type="submit" disabled={creating} className="gold-gradient text-black font-semibold" data-testid="submit-invoice-btn">
              {creating ? 'Creating...' : 'Create Invoice'}
            </Button>
            <Button type="button" variant="outline" onClick={() => { setShowCreate(false); setLineItems([{ ...emptyItem }]); }} className="border-white/20 text-white/60">
              Cancel
            </Button>
          </div>
        </form>
      )}

      {/* Invoice Preview Modal */}
      {previewInvoice && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4" onClick={() => setPreviewInvoice(null)}>
          <div className="bg-white rounded-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()} data-testid="invoice-preview-modal">
            {/* Printable Invoice */}
            <div className="p-8" id="invoice-print-area">
              {/* Header */}
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">2nd Team Casting</h2>
                  <p className="text-gray-500 text-sm mt-1">Stand-In Casting Agency</p>
                </div>
                <div className="text-right">
                  <h3 className="text-xl font-bold text-gray-900">{previewInvoice.invoice_number}</h3>
                  <p className="text-gray-500 text-sm">Date: {formatDateUK(previewInvoice.submitted_date || previewInvoice.created_at)}</p>
                  {previewInvoice.due_date && <p className="text-gray-500 text-sm">Due: {formatDateUK(previewInvoice.due_date)}</p>}
                  <span className={`inline-block mt-1 px-2 py-0.5 text-xs rounded capitalize font-semibold ${
                    previewInvoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                    previewInvoice.status === 'sent' ? 'bg-blue-100 text-blue-700' :
                    'bg-gray-100 text-gray-600'
                  }`}>{previewInvoice.status}</span>
                </div>
              </div>

              {/* Bill To */}
              <div className="mb-6 pb-4 border-b border-gray-200">
                <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Bill To</p>
                <p className="text-gray-900 font-semibold">{previewInvoice.client_name}</p>
                {previewInvoice.client_address && <p className="text-gray-600 text-sm">{previewInvoice.client_address}</p>}
                <p className="text-gray-600 text-sm mt-1">Project: <strong>{previewInvoice.project_name}</strong></p>
              </div>

              {/* Line Items Table */}
              <table className="w-full mb-6">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Stand-in</th>
                    <th className="text-left py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Role</th>
                    <th className="text-left py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Date</th>
                    <th className="text-right py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Rate</th>
                    <th className="text-right py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Qty</th>
                    <th className="text-right py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Amount</th>
                    <th className="text-center py-2 text-gray-600 text-xs uppercase tracking-wider font-semibold">Paid</th>
                  </tr>
                </thead>
                <tbody>
                  {previewInvoice.line_items?.map((item, idx) => (
                    <tr key={idx} className="border-b border-gray-100">
                      <td className="py-2 text-gray-900 text-sm">{item.standin_name}</td>
                      <td className="py-2 text-gray-600 text-sm">{item.role}</td>
                      <td className="py-2 text-gray-600 text-sm">{formatDateUK(item.date_worked)}</td>
                      <td className="py-2 text-gray-900 text-sm text-right">£{parseFloat(item.day_rate).toFixed(2)}</td>
                      <td className="py-2 text-gray-900 text-sm text-right">{item.quantity}</td>
                      <td className="py-2 text-gray-900 text-sm text-right font-medium">£{parseFloat(item.amount).toFixed(2)}</td>
                      <td className="py-2 text-center">
                        <button
                          onClick={() => updateLineItemPayment(previewInvoice.id, idx, item.payment_status === 'paid' ? 'unpaid' : 'paid')}
                          className={`px-2 py-0.5 text-xs rounded font-medium cursor-pointer ${
                            item.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500 hover:bg-yellow-50 hover:text-yellow-700'
                          }`}
                          data-testid={`toggle-payment-${idx}`}
                        >
                          {item.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64">
                  <div className="flex justify-between py-1 text-sm">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-gray-900">£{parseFloat(previewInvoice.subtotal).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between py-2 border-t-2 border-gray-900 mt-1 font-bold text-lg">
                    <span className="text-gray-900">Total</span>
                    <span className="text-gray-900">£{parseFloat(previewInvoice.total).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              {previewInvoice.notes && (
                <div className="mt-6 pt-4 border-t border-gray-200">
                  <p className="text-gray-500 text-xs uppercase tracking-wider mb-1">Notes</p>
                  <p className="text-gray-600 text-sm">{previewInvoice.notes}</p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex items-center justify-between px-8 py-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
              <div className="flex gap-2">
                {previewInvoice.status === 'draft' && (
                  <Button size="sm" onClick={() => { updateStatus(previewInvoice.id, 'sent'); setPreviewInvoice({ ...previewInvoice, status: 'sent' }); }} className="bg-blue-600 hover:bg-blue-700 text-white text-xs" data-testid="preview-mark-sent">
                    <Send className="w-3 h-3 mr-1" /> Mark Sent
                  </Button>
                )}
                {(previewInvoice.status === 'sent' || previewInvoice.status === 'draft') && (
                  <Button size="sm" onClick={() => { updateStatus(previewInvoice.id, 'paid'); setPreviewInvoice({ ...previewInvoice, status: 'paid' }); }} className="bg-green-600 hover:bg-green-700 text-white text-xs" data-testid="preview-mark-paid">
                    <CheckCircle className="w-3 h-3 mr-1" /> Mark Paid
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => {
                  window.open(`${API}/admin/invoices/${previewInvoice.id}/pdf?token=${token}`, '_blank');
                }} className="text-gray-600 border-gray-300 text-xs" data-testid="download-pdf-btn">
                  <ExternalLink className="w-3 h-3 mr-1" /> Download PDF
                </Button>
                <Button size="sm" variant="outline" onClick={() => { duplicateInvoice(previewInvoice.id); setPreviewInvoice(null); }} className="text-gray-600 border-gray-300 text-xs" data-testid="duplicate-from-preview-btn">
                  <Copy className="w-3 h-3 mr-1" /> Duplicate
                </Button>
                <Button size="sm" variant="outline" onClick={() => window.print()} className="text-gray-600 border-gray-300 text-xs" data-testid="print-invoice-btn">
                  Print
                </Button>
                <Button size="sm" variant="outline" onClick={() => setPreviewInvoice(null)} className="text-gray-600 border-gray-300 text-xs" data-testid="close-preview-btn">
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Invoice List */}
      {loading ? (
        <div className="flex items-center justify-center h-40"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>
      ) : invoices.length === 0 ? (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-12 text-center">
          <FileText className="w-12 h-12 text-white/20 mx-auto mb-4" />
          <p className="text-white/40">No invoices yet. Create your first invoice above.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => {
            const paidCount = inv.line_items?.filter(i => i.payment_status === 'paid').length || 0;
            const totalItems = inv.line_items?.length || 0;

            return (
              <div key={inv.id} className="bg-[#121212] border border-white/5 rounded-xl p-5 hover:border-white/10 transition-colors" data-testid={`invoice-${inv.id}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <span className="text-[#D4AF37] font-mono text-sm">{inv.invoice_number}</span>
                      <span className={`px-2 py-0.5 text-xs rounded capitalize ${
                        inv.status === 'paid' ? 'bg-[#10B981]/20 text-[#10B981]' :
                        inv.status === 'sent' ? 'bg-blue-500/20 text-blue-400' :
                        inv.status === 'draft' ? 'bg-white/10 text-white/60' :
                        'bg-[#F59E0B]/20 text-[#F59E0B]'
                      }`}>{inv.status}</span>
                      {totalItems > 0 && (
                        <span className="text-white/30 text-xs">{paidCount}/{totalItems} paid</span>
                      )}
                    </div>
                    <h3 className="text-white font-semibold">{inv.client_name} — {inv.project_name}</h3>
                    <p className="text-white/40 text-sm mt-0.5">{totalItems} line item{totalItems !== 1 ? 's' : ''} | Submitted: {inv.submitted_date || ''}</p>
                    {/* Show stand-in names as pills */}
                    <div className="flex flex-wrap gap-1 mt-2">
                      {[...new Set(inv.line_items?.map(i => i.standin_name))].map((name, i) => (
                        <span key={i} className="px-2 py-0.5 bg-white/5 text-white/50 text-xs rounded">{name}</span>
                      ))}
                    </div>
                  </div>
                  <div className="text-right ml-4">
                    <p className="text-2xl font-bold text-[#D4AF37]">£{parseFloat(inv.total).toFixed(2)}</p>
                    <div className="flex gap-1 mt-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setPreviewInvoice(inv)} className="text-white/60 hover:text-white text-xs" data-testid={`view-invoice-${inv.id}`}>
                        <Eye className="w-4 h-4 mr-1" /> View
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => duplicateInvoice(inv.id)} className="text-white/40 hover:text-[#D4AF37] text-xs" data-testid={`dup-invoice-${inv.id}`}>
                        <Copy className="w-4 h-4 mr-1" /> Duplicate
                      </Button>
                      {inv.status === 'draft' && (
                        <Button size="sm" onClick={() => updateStatus(inv.id, 'sent')} className="bg-blue-500/20 text-blue-400 text-xs hover:bg-blue-500/30" data-testid={`send-invoice-${inv.id}`}>Mark Sent</Button>
                      )}
                      {(inv.status === 'sent' || inv.status === 'draft') && (
                        <Button size="sm" onClick={() => updateStatus(inv.id, 'paid')} className="bg-[#10B981]/20 text-[#10B981] text-xs hover:bg-[#10B981]/30" data-testid={`pay-invoice-${inv.id}`}>Mark Paid</Button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Google Integration Settings
const GoogleSettings = ({ token }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [sheetName, setSheetName] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]);
  const [previewing, setPreviewing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [waStatus, setWaStatus] = useState(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test message from 2nd Team Casting.');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchSyncHistory();
    fetchWhatsAppStatus();
  }, []);

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/google-status`, { headers: { Authorization: `Bearer ${token}` } });
      setStatus(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchSyncHistory = async () => {
    try {
      const res = await axios.get(`${API}/admin/sheets/sync-history`, { headers: { Authorization: `Bearer ${token}` } });
      setSyncHistory(res.data);
      if (res.data.length > 0) {
        setSpreadsheetId(res.data[0].spreadsheet_id || '');
      }
    } catch (e) { /* ignore if not connected */ }
  };

  const connectGmail = async () => {
    try {
      const res = await axios.get(`${API}/oauth/gmail/login`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.auth_url;
    } catch (e) { toast.error('Gmail OAuth not available. Check Google credentials in settings.'); }
  };

  const connectSheets = async () => {
    try {
      const res = await axios.get(`${API}/oauth/sheets/login`, { headers: { Authorization: `Bearer ${token}` } });
      window.location.href = res.data.auth_url;
    } catch (e) { toast.error('Sheets OAuth not available. Check Google credentials in settings.'); }
  };

  const fetchWhatsAppStatus = async () => {
    try {
      const res = await axios.get(`${API}/admin/whatsapp/status`, { headers: { Authorization: `Bearer ${token}` } });
      setWaStatus(res.data);
    } catch (e) { /* ignore */ }
  };

  const sendTestWhatsApp = async () => {
    if (!testPhone.trim()) {
      toast.error('Enter a phone number');
      return;
    }
    setSendingTest(true);
    setTestResult(null);
    try {
      const params = new URLSearchParams({ phone: testPhone.trim().replace('+', '').replace(/\s/g, ''), message: testMessage });
      const res = await axios.post(`${API}/admin/whatsapp/test?${params}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setTestResult(res.data);
      if (res.data.success) {
        toast.success('WhatsApp message sent!');
      } else {
        toast.error(res.data.error?.message || 'Send failed');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send');
    } finally { setSendingTest(false); }
  };

  const previewSheet = async () => {
    if (!spreadsheetId.trim()) {
      toast.error('Enter a Spreadsheet ID');
      return;
    }
    setPreviewing(true);
    setPreviewData(null);
    try {
      const params = new URLSearchParams({ spreadsheet_id: spreadsheetId.trim() });
      if (sheetName.trim()) params.append('sheet_name', sheetName.trim());
      const res = await axios.get(`${API}/admin/sheets/sync?${params}`, { headers: { Authorization: `Bearer ${token}` } });
      setPreviewData(res.data);
      toast.success(`Found ${res.data.total_rows} rows`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to read sheet');
    } finally { setPreviewing(false); }
  };

  const syncProfiles = async () => {
    if (!spreadsheetId.trim()) {
      toast.error('Enter a Spreadsheet ID');
      return;
    }
    setSyncing(true);
    setSyncResult(null);
    try {
      const params = new URLSearchParams({ spreadsheet_id: spreadsheetId.trim() });
      if (sheetName.trim()) params.append('sheet_name', sheetName.trim());
      const res = await axios.post(`${API}/admin/sheets/sync-profiles?${params}`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setSyncResult(res.data);
      toast.success(res.data.message);
      fetchSyncHistory();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Sync failed');
    } finally { setSyncing(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#D4AF37]"></div></div>;

  return (
    <div className="max-w-4xl">
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Settings</h1>
      <p className="text-white/60 mb-8">Connect Google services for email notifications and spreadsheet sync</p>

      {!status?.credentials_configured && (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-xl p-6 mb-6">
          <h2 className="text-[#F59E0B] font-semibold mb-2">Google Credentials Required</h2>
          <p className="text-[#F59E0B]/80 text-sm mb-4">To connect Gmail and Google Sheets, you need to set up OAuth credentials in the Google Cloud Console:</p>
          <ol className="text-white/60 text-sm space-y-2 list-decimal list-inside">
            <li>Go to <a href="https://console.cloud.google.com" target="_blank" rel="noreferrer" className="text-[#D4AF37] underline">Google Cloud Console</a></li>
            <li>Create a project (or select existing)</li>
            <li>Enable <strong>Gmail API</strong> and <strong>Google Sheets API</strong> in APIs & Services &rarr; Library</li>
            <li>Go to APIs & Services &rarr; OAuth consent screen &rarr; External &rarr; fill in app name + emails</li>
            <li>Go to Credentials &rarr; Create OAuth client ID &rarr; Web application</li>
            <li className="font-medium text-white/80">
              Add redirect URIs:
              <code className="block bg-[#0A0A0A] p-2 rounded mt-1 text-[#D4AF37] text-xs break-all">
                {window.location.origin}/api/oauth/gmail/callback
              </code>
              <code className="block bg-[#0A0A0A] p-2 rounded mt-1 text-[#D4AF37] text-xs break-all">
                {window.location.origin}/api/oauth/sheets/callback
              </code>
            </li>
            <li>Copy the <strong>Client ID</strong> and <strong>Client Secret</strong> and provide them to your developer to add to the backend .env file</li>
          </ol>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Gmail */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#EA4335]/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#EA4335]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Gmail</h3>
              <p className="text-white/40 text-xs">Send notifications to stand-ins</p>
            </div>
          </div>
          {status?.gmail_connected ? (
            <div className="flex items-center gap-2 text-[#10B981] text-sm">
              <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
              Connected
            </div>
          ) : (
            <Button onClick={connectGmail} disabled={!status?.credentials_configured} className="w-full bg-[#EA4335] hover:bg-[#EA4335]/80 text-white" data-testid="connect-gmail-btn">
              Connect Gmail
            </Button>
          )}
        </div>

        {/* Google Sheets */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#34A853]/20 flex items-center justify-center">
              <FileText className="w-5 h-5 text-[#34A853]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">Google Sheets</h3>
              <p className="text-white/40 text-xs">Sync stand-in database</p>
            </div>
          </div>
          {status?.sheets_connected ? (
            <div className="flex items-center gap-2 text-[#10B981] text-sm">
              <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
              Connected
            </div>
          ) : (
            <Button onClick={connectSheets} disabled={!status?.credentials_configured} className="w-full bg-[#34A853] hover:bg-[#34A853]/80 text-white" data-testid="connect-sheets-btn">
              Connect Google Sheets
            </Button>
          )}
        </div>

        {/* WhatsApp */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-[#25D366]/20 flex items-center justify-center">
              <Send className="w-5 h-5 text-[#25D366]" />
            </div>
            <div>
              <h3 className="text-white font-semibold">WhatsApp</h3>
              <p className="text-white/40 text-xs">Message stand-ins directly</p>
            </div>
          </div>
          {waStatus?.configured ? (
            <div className="flex items-center gap-2 text-[#10B981] text-sm">
              <div className="w-2 h-2 rounded-full bg-[#10B981]"></div>
              Connected
            </div>
          ) : (
            <div className="flex items-center gap-2 text-[#F59E0B] text-sm">
              <div className="w-2 h-2 rounded-full bg-[#F59E0B]"></div>
              Not configured
            </div>
          )}
        </div>
      </div>

      {/* Google Sheets Sync */}
      {status?.sheets_connected && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-1">Live Database Sync</h2>
          <p className="text-white/40 text-sm mb-6">Pull stand-in profiles from your Google Sheet into the matching database. Matching priority: Height &gt; Hair Colour &gt; Skin Tone &gt; Gender.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/80 mb-2 block">Spreadsheet ID *</Label>
              <Input
                value={spreadsheetId}
                onChange={(e) => setSpreadsheetId(e.target.value)}
                placeholder="e.g., 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="spreadsheet-id-input"
              />
              <p className="text-white/30 text-xs mt-1">Found in the Sheet URL between /d/ and /edit</p>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Sheet Tab Name (optional)</Label>
              <Input
                value={sheetName}
                onChange={(e) => setSheetName(e.target.value)}
                placeholder="e.g., Sheet1 or Stand-ins"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="sheet-name-input"
              />
              <p className="text-white/30 text-xs mt-1">Leave blank for the first tab</p>
            </div>
          </div>

          <div className="flex gap-3 mb-6">
            <Button
              onClick={previewSheet}
              disabled={previewing || !spreadsheetId.trim()}
              variant="outline"
              className="border-[#D4AF37]/30 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              data-testid="preview-sheet-btn"
            >
              {previewing ? 'Loading...' : 'Preview Sheet'}
            </Button>
            <Button
              onClick={syncProfiles}
              disabled={syncing || !spreadsheetId.trim()}
              className="gold-gradient text-black font-bold hover:brightness-110"
              data-testid="sync-profiles-btn"
            >
              {syncing ? 'Syncing...' : 'Sync Profiles Now'}
            </Button>
          </div>

          {/* Preview Data */}
          {previewData && (
            <div className="bg-[#0A0A0A] border border-white/10 rounded-lg p-4 mb-6">
              <h3 className="text-white font-semibold mb-2 text-sm">Sheet Preview ({previewData.total_rows} rows)</h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {previewData.headers?.map((h, i) => (
                        <th key={i} className="text-left text-[#D4AF37]/70 px-2 py-1 border-b border-white/5 whitespace-nowrap">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.sample?.map((row, ri) => (
                      <tr key={ri}>
                        {previewData.headers?.map((_, ci) => (
                          <td key={ci} className="text-white/60 px-2 py-1 border-b border-white/5 whitespace-nowrap max-w-[200px] truncate">{row[ci] || ''}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Sync Result */}
          {syncResult && (
            <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-lg p-4 mb-6">
              <h3 className="text-[#10B981] font-semibold mb-3">Sync Complete</h3>
              <div className="grid grid-cols-3 gap-4 mb-3">
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{syncResult.imported}</p>
                  <p className="text-white/40 text-xs">New Profiles</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{syncResult.updated}</p>
                  <p className="text-white/40 text-xs">Updated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-white">{syncResult.skipped}</p>
                  <p className="text-white/40 text-xs">Skipped</p>
                </div>
              </div>
              {syncResult.db_stats && (
                <div className="border-t border-white/10 pt-3">
                  <p className="text-white/50 text-xs mb-1">Database Stats After Sync:</p>
                  <p className="text-white/70 text-xs">Total: {syncResult.db_stats.total_profiles} profiles &middot; With Height: {syncResult.db_stats.with_height} &middot; With Hair: {syncResult.db_stats.with_hair}</p>
                </div>
              )}
              {syncResult.detected_columns && (
                <div className="border-t border-white/10 pt-3 mt-3">
                  <p className="text-white/50 text-xs mb-1">Detected Columns:</p>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(syncResult.detected_columns).filter(([,v]) => v).map(([k, v]) => (
                      <span key={k} className="px-2 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37] text-xs rounded">{k}: {v}</span>
                    ))}
                  </div>
                </div>
              )}
              {syncResult.errors?.length > 0 && (
                <div className="border-t border-white/10 pt-3 mt-3">
                  <p className="text-red-400 text-xs mb-1">Errors ({syncResult.errors.length}):</p>
                  {syncResult.errors.map((e, i) => (
                    <p key={i} className="text-red-400/70 text-xs">{e}</p>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Sync History */}
          {syncHistory.length > 0 && (
            <div>
              <h3 className="text-white/60 text-xs uppercase tracking-wider mb-3">Sync History</h3>
              <div className="space-y-2">
                {syncHistory.slice(0, 5).map((log, i) => (
                  <div key={i} className="flex items-center justify-between bg-[#0A0A0A] rounded-lg px-4 py-2 text-xs">
                    <span className="text-white/50">{new Date(log.synced_at).toLocaleString('en-GB')}</span>
                    <div className="flex gap-4">
                      <span className="text-[#10B981]">+{log.imported} new</span>
                      <span className="text-[#3B82F6]">{log.updated} updated</span>
                      <span className="text-white/30">{log.skipped} skipped</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* WhatsApp Test */}
      {waStatus?.configured && (
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 mb-6">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-1">WhatsApp Messaging</h2>
          <p className="text-white/40 text-sm mb-6">Send a test message to verify WhatsApp is working. Stand-ins will receive WhatsApp notifications for availability checks automatically.</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="text-white/80 mb-2 block">Phone Number *</Label>
              <Input
                value={testPhone}
                onChange={(e) => setTestPhone(e.target.value)}
                placeholder="e.g., 447455234106"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="wa-test-phone"
              />
              <p className="text-white/30 text-xs mt-1">Country code + number, no spaces or +</p>
            </div>
            <div>
              <Label className="text-white/80 mb-2 block">Test Message</Label>
              <Input
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                placeholder="Hello from 2nd Team!"
                className="bg-[#0A0A0A] border-white/10 text-white h-12"
                data-testid="wa-test-message"
              />
            </div>
          </div>

          <Button
            onClick={sendTestWhatsApp}
            disabled={sendingTest || !testPhone.trim()}
            className="bg-[#25D366] hover:bg-[#25D366]/80 text-white font-bold"
            data-testid="wa-send-test-btn"
          >
            <Send className="w-4 h-4 mr-2" />
            {sendingTest ? 'Sending...' : 'Send Test Message'}
          </Button>

          {testResult && (
            <div className={`mt-4 rounded-lg p-4 ${testResult.success ? 'bg-[#10B981]/10 border border-[#10B981]/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <p className={`font-semibold text-sm ${testResult.success ? 'text-[#10B981]' : 'text-red-400'}`}>
                {testResult.success ? 'Message Sent Successfully' : 'Send Failed'}
              </p>
              <p className="text-white/50 text-xs mt-1">{testResult.message}</p>
              {testResult.message_id && <p className="text-white/30 text-xs mt-1">ID: {testResult.message_id}</p>}
              {testResult.error?.message && <p className="text-red-400/70 text-xs mt-1">{testResult.error.message}</p>}
            </div>
          )}
        </div>
      )}
    </div>
  );
};


// Share Profiles
const ShareProfiles = ({ token }) => {
  const [standins, setStandins] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchStandins();
  }, []);

  const fetchStandins = async () => {
    try {
      const response = await axios.get(`${API}/admin/all-standins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStandins(response.data.filter(s => s.status === 'approved'));
    } catch (error) {
      console.error('Error fetching stand-ins:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const createShareLink = async () => {
    if (selectedIds.length === 0) {
      toast.error('Select at least one stand-in');
      return;
    }

    setCreating(true);
    try {
      const response = await axios.post(`${API}/share/create`, {
        standin_ids: selectedIds,
        expires_in_hours: 24
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const link = `${window.location.origin}/share/${response.data.share_token}`;
      setShareLink(link);
      toast.success('Share link created!');
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(shareLink);
    toast.success('Link copied to clipboard!');
  };

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Share Profiles</h1>
      <p className="text-white/60 mb-8">Create a shareable link with selected stand-in profiles (contact info hidden)</p>

      {shareLink && (
        <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 mb-6">
          <p className="text-[#10B981] text-sm mb-2">Share link created (expires in 24 hours):</p>
          <p className="text-white/40 text-xs mb-2">Only shows: First name, Height, Experience level</p>
          <div className="flex gap-2">
            <Input value={shareLink} readOnly className="bg-[#0A0A0A] border-white/10 text-white flex-1" />
            <Button onClick={copyLink} className="gold-gradient text-black">
              <Copy className="w-4 h-4 mr-2" />
              Copy
            </Button>
            <Button variant="outline" onClick={() => window.open(shareLink, '_blank')} className="border-white/20 text-white">
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <p className="text-white/60">{selectedIds.length} selected</p>
        <Button
          onClick={createShareLink}
          disabled={creating || selectedIds.length === 0}
          className="gold-gradient text-black font-semibold"
          data-testid="create-share-link"
        >
          <Share2 className="w-4 h-4 mr-2" />
          {creating ? 'Creating...' : 'Create Share Link'}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
        {standins.map((standin) => (
          <div 
            key={standin.id}
            onClick={() => toggleSelect(standin.id)}
            className={`bg-[#121212] border rounded-xl p-4 cursor-pointer transition-colors ${
              selectedIds.includes(standin.id) ? 'border-[#D4AF37]' : 'border-white/5 hover:border-white/20'
            }`}
          >
            <div className="flex items-center gap-3">
              <Checkbox 
                checked={selectedIds.includes(standin.id)}
                className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
              />
              {standin.headshot_url ? (
                <img src={standin.headshot_url} alt="" className="w-12 h-12 rounded-lg object-cover" />
              ) : (
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                  <span className="text-[#D4AF37] font-semibold">{standin.full_name?.charAt(0)}</span>
                </div>
              )}
              <div>
                <p className="text-white font-medium">{standin.full_name}</p>
                <p className="text-white/40 text-sm capitalize">{standin.gender} • {standin.hair_color}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Chit Calculator
const ChitCalculator = ({ token }) => {
  const [formData, setFormData] = useState({
    base_rate: 250,
    call_time: '07:00',
    wrap_time: '19:00',
    lunch_break: true,
    early_call: false,
    night_shoot: false,
    holiday_pay_percent: 12.07
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const calculateChit = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/chit/calculate`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="font-['Playfair_Display'] text-3xl font-bold text-white mb-2">Chit Calculator</h1>
      <p className="text-white/60 mb-8">Calculate stand-in pay based on UK film industry rates</p>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-semibold text-white mb-4">Basic Details</h2>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/80 mb-2 block">Base Rate (£)</Label>
                <Input
                  type="number"
                  value={formData.base_rate}
                  onChange={(e) => setFormData({ ...formData, base_rate: parseFloat(e.target.value) })}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="chit-base-rate"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Holiday Pay %</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={formData.holiday_pay_percent}
                  onChange={(e) => setFormData({ ...formData, holiday_pay_percent: parseFloat(e.target.value) })}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="chit-holiday-pay"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Call Time</Label>
                <Input
                  type="time"
                  value={formData.call_time}
                  onChange={(e) => setFormData({ ...formData, call_time: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="chit-call-time"
                />
              </div>
              <div>
                <Label className="text-white/80 mb-2 block">Wrap Time</Label>
                <Input
                  type="time"
                  value={formData.wrap_time}
                  onChange={(e) => setFormData({ ...formData, wrap_time: e.target.value })}
                  className="bg-[#0A0A0A] border-white/10 text-white"
                  data-testid="chit-wrap-time"
                />
              </div>
            </div>
          </div>

          <div className="bg-[#121212] border border-white/5 rounded-xl p-6">
            <h2 className="font-semibold text-white mb-4">Options</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.lunch_break}
                  onCheckedChange={(c) => setFormData({ ...formData, lunch_break: c })}
                  className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                />
                <span className="text-white">Lunch Break (1 hour deducted)</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.early_call}
                  onCheckedChange={(c) => setFormData({ ...formData, early_call: c })}
                  className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                />
                <span className="text-white">Early Call (before 7am) +10%</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox
                  checked={formData.night_shoot}
                  onCheckedChange={(c) => setFormData({ ...formData, night_shoot: c })}
                  className="data-[state=checked]:bg-[#D4AF37] data-[state=checked]:border-[#D4AF37]"
                />
                <span className="text-white">Night Shoot +15%</span>
              </label>
            </div>
          </div>

          <Button
            onClick={calculateChit}
            disabled={loading}
            className="w-full gold-gradient text-black font-bold py-3 uppercase tracking-wider"
            data-testid="calculate-chit-btn"
          >
            <Calculator className="w-4 h-4 mr-2" />
            {loading ? 'Calculating...' : 'Calculate'}
          </Button>
        </div>

        {/* Results */}
        <div className="bg-[#121212] border border-white/5 rounded-xl p-6 h-fit">
          <h2 className="font-['Playfair_Display'] text-xl font-bold text-white mb-6">Calculation Summary</h2>
          
          {result ? (
            <div className="space-y-4">
              <div className="flex justify-between py-3 border-b border-white/10">
                <span className="text-white/60">Base Pay</span>
                <span className="text-white font-medium">£{result.base_pay.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/10">
                <span className="text-white/60">Hours Worked</span>
                <span className="text-white font-medium">{result.breakdown?.hours_worked} hrs</span>
              </div>
              <div className="flex justify-between py-3 border-b border-white/10">
                <span className="text-white/60">Overtime ({result.breakdown?.overtime_hours} hrs)</span>
                <span className="text-white font-medium">£{result.overtime_pay.toFixed(2)}</span>
              </div>
              {result.breakdown?.early_call_premium > 0 && (
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Early Call Premium</span>
                  <span className="text-white font-medium">£{result.breakdown.early_call_premium.toFixed(2)}</span>
                </div>
              )}
              {result.breakdown?.night_premium > 0 && (
                <div className="flex justify-between py-3 border-b border-white/10">
                  <span className="text-white/60">Night Shoot Premium</span>
                  <span className="text-white font-medium">£{result.breakdown.night_premium.toFixed(2)}</span>
                </div>
              )}
              <div className="flex justify-between py-3 border-b border-white/10">
                <span className="text-white/60">Holiday Pay (12.07%)</span>
                <span className="text-white font-medium">£{result.holiday_pay.toFixed(2)}</span>
              </div>
              <div className="flex justify-between py-4 border-t-2 border-[#D4AF37]">
                <span className="text-white font-semibold">GROSS TOTAL</span>
                <span className="text-[#D4AF37] font-['Playfair_Display'] text-3xl font-bold">£{result.gross_total.toFixed(2)}</span>
              </div>
            </div>
          ) : (
            <div className="text-center py-12 text-white/40">
              <Calculator className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Enter details and click Calculate</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
